import { useRouter } from "next/router";
import {
  Button,
  Link as LinkUi,
  VideoObjectProps,
} from "@business-finland/wif-ui-lib";
import {
  Hero as HeroUi,
  type HeroVariant,
} from "@business-finland/wif-ui-lib/dist/Hero";

import useViewport from "../../../contexts/ViewportContext";
import useEmployerSite from "../../../contexts/EmployerSiteContext";
import useImageDataHandler from "../../../hooks/useImageDataHandler";
import {
  formatPageLink,
  getAssetPath,
  jcrMultiFieldToArray,
} from "../../../util/magnolia";

export type IHeroAssetProps =
  | {
      field: "video";
      videoXS: Wif.Mgnl.Video;
      videoSM: Wif.Mgnl.Video;
      videoLG: Wif.Mgnl.Video;
      videoAltText?: string;
    }
  | {
      field: "image";
      image: Wif.Mgnl.Image;
      imageAltText?: string;
    };
export interface IHeroButtonProps {
  text: string;
  link: string;
  type: "primary" | "secondary";
  anchorTag?: string;
}

export interface IHeroProps {
  heading: string;
  asset: IHeroAssetProps;
  buttons: Wif.Mgnl.NodeWithList<Wif.Mgnl.Node<IHeroButtonProps>>;
  leadText?: string;
  variant?: HeroVariant;
}

export function Hero({
  heading,
  leadText,
  asset,
  buttons,
  variant = "default",
}: IHeroProps): JSX.Element {
  const router = useRouter();
  const { viewport } = useViewport();
  const { isEmployerSite } = useEmployerSite();
  const imageDataHandler = useImageDataHandler();
  const isContentBelowImage = variant === "contentBelowImage";

  const buttonObjects = jcrMultiFieldToArray(buttons);
  const buttonLinks = buttonObjects.map(
    (button: IHeroButtonProps, index: number) => {
      const link = formatPageLink(
        button.link,
        router?.isPreview,
        button.anchorTag
      );

      const HeroButton = resolveHeroButton(
        isContentBelowImage,
        isEmployerSite,
        button.type,
        index
      );

      return (
        <LinkUi
          className={Button.DefaultFocusStyleInLink}
          href={link}
          key={button.text}
          disableLinkTextStyle={true}
          itemProp="url"
        >
          <HeroButton asDiv text={button.text} />
        </LinkUi>
      );
    }
  );

  if (asset.field === "video") {
    const { videoXS, videoSM, videoLG, videoAltText } = asset;

    const videoSrc: VideoObjectProps[] = [
      {
        src: getAssetPath(videoXS, router.isPreview),
        mimeType: videoXS.metadata?.mimeType,
        device: ["xs"],
        credits: videoXS?.metadata?.rights,
      },
      {
        src: getAssetPath(videoSM, router.isPreview),
        mimeType: videoSM.metadata?.mimeType,
        device: ["sm", "md"],
        credits: videoSM?.metadata?.rights,
      },
      {
        src: getAssetPath(videoLG, router.isPreview),
        mimeType: videoLG.metadata?.mimeType,
        device: ["lg", "xl"],
        credits: videoLG?.metadata?.rights,
      },
    ];

    return (
      <div>
        <HeroUi
          assetType={asset.field}
          heading={heading}
          leadText={leadText}
          video={videoSrc}
          viewport={viewport}
          videoAltText={videoAltText}
          buttons={buttonLinks}
          variant={variant}
        />
      </div>
    );
  }

  const { image, imageAltText } = asset;
  const imageData = imageDataHandler(image);

  return (
    <div>
      <HeroUi
        imageData={{ ...imageData, imageSrc: imageData.src }}
        assetType={asset.field}
        heading={heading}
        leadText={leadText}
        imageAltText={imageAltText}
        buttons={buttonLinks}
        credits={image?.metadata?.rights}
        variant={variant}
      />
    </div>
  );
}

function resolveHeroButton(
  isContentBelowImage: boolean,
  isEmployerSite: boolean,
  type: IHeroButtonProps["type"] | undefined,
  index: number
) {
  if (!isContentBelowImage) {
    return isEmployerSite ? Button.HeroEmployer : Button.HeroTalents;
  }

  const isSecondary = type === "secondary" || (!type && index > 0);
  return isSecondary ? Button.Secondary : Button.Primary;
}
